import { useMemo, useState } from 'react';
import type { Client, Contact } from '../../types/crm';
import { AccountDetailProfile } from './AccountDetailProfile';
import { contactLabel, idsMatch } from '../../utils/crmHelpers';
import { buildContactDedupKey, type DedupedAccountContact } from '../../utils/contactDedup';
import { SIDEBAR_LIST_PREVIEW, takePreview } from '../../utils/accountSidebarList';
import { ContactDetailModal } from '../contacts/ContactDetailModal';
import { Modal } from '../ui/Modal';

interface AccountDetailSidebarProps {
  client: Client;
  salespersonCode: string;
  hideSalesperson?: boolean;
  contacts: DedupedAccountContact[];
  canEdit: boolean;
  acraStatus?: string | null;
  onAddContact: () => void;
  onEditContact: (c: Contact) => void;
}

export function AccountDetailSidebar({
  client,
  salespersonCode,
  hideSalesperson = false,
  contacts,
  canEdit,
  acraStatus = null,
  onAddContact,
  onEditContact,
}: AccountDetailSidebarProps) {
  const [viewAllContacts, setViewAllContacts] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<DedupedAccountContact | null>(null);

  const sortedContacts = useMemo(
    () =>
      [...contacts].sort((a, b) =>
        contactLabel(a.contact).localeCompare(contactLabel(b.contact)),
      ),
    [contacts],
  );
  const previewContacts = takePreview(sortedContacts);
  const hiddenContactCount = Math.max(0, sortedContacts.length - SIDEBAR_LIST_PREVIEW);

  function renderContactRow(entry: DedupedAccountContact, inModal = false) {
    const { contact, brandNames, relatedContacts } = entry;
    const canEditContact =
      canEdit && relatedContacts.some((row) => idsMatch(row.client_id, client.id));

    return (
      <li key={buildContactDedupKey(contact)} className="account-side-list__row">
        <div className="account-side-list__body">
          <button
            type="button"
            className="account-side-list__name account-side-list__name--button"
            onClick={() => setSelectedEntry(entry)}
          >
            {contactLabel(contact)}
          </button>
          {contact.job_title && <span className="account-side-muted">{contact.job_title}</span>}
          {brandNames.length > 0 && (
            <span className="account-side-list__company">{brandNames.join(' · ')}</span>
          )}
        </div>
        {canEditContact && (
          <button
            type="button"
            className="account-side-btn account-side-btn--muted"
            onClick={() => {
              if (inModal) setViewAllContacts(false);
              const editable =
                relatedContacts.find((row) => idsMatch(row.client_id, client.id)) ?? contact;
              onEditContact(editable);
            }}
          >
            Edit
          </button>
        )}
      </li>
    );
  }

  return (
    <>
      <aside className="account-detail-col account-detail-col--left">
        <div className="account-detail-profile--sidebar">
          <AccountDetailProfile
            client={client}
            salespersonCode={salespersonCode}
            hideSalesperson={hideSalesperson}
            acraStatus={acraStatus}
          />
        </div>

        <section className="account-side-card">
          <div className="account-side-card__head">
            <h2 className="account-side-card__title">Contacts ({contacts.length})</h2>
            {canEdit && (
              <button type="button" className="account-side-btn" onClick={onAddContact}>
                Add
              </button>
            )}
          </div>
          {contacts.length === 0 ? (
            <p className="account-side-muted">None yet.</p>
          ) : (
            <>
              <ul className="account-side-list">{previewContacts.map((entry) => renderContactRow(entry))}</ul>
              {hiddenContactCount > 0 && (
                <button
                  type="button"
                  className="account-side-view-all"
                  onClick={() => setViewAllContacts(true)}
                >
                  View all ({hiddenContactCount})
                </button>
              )}
            </>
          )}
        </section>
      </aside>

      <Modal
        title={`Contacts · ${client.company_name}`}
        open={viewAllContacts}
        onClose={() => setViewAllContacts(false)}
        wide
      >
        <ul className="account-side-list account-side-list--modal">
          {sortedContacts.map((entry) => renderContactRow(entry, true))}
        </ul>
      </Modal>

      <ContactDetailModal
        open={selectedEntry != null}
        entry={selectedEntry}
        salespersonCode={salespersonCode}
        hideSalesperson={hideSalesperson}
        canEdit={
          selectedEntry != null &&
          canEdit &&
          selectedEntry.relatedContacts.some((row) => idsMatch(row.client_id, client.id))
        }
        onClose={() => setSelectedEntry(null)}
        onEdit={(contact) => {
          const editable =
            selectedEntry?.relatedContacts.find((row) => idsMatch(row.client_id, client.id)) ??
            contact;
          onEditContact(editable);
        }}
      />
    </>
  );
}
